import TF from "@/components/forms/TextField.module.css";
import C from "@/modules/main/debug/DebugMenu.module.css";

import { WTooltipButton } from "@/components/button/WToolButton.tsx";
import { SimpleSwitch } from "@/components/dev/SimpleSwitch.tsx";
import { WSimpleSelect, toSimpleItem } from "@/components/dev/WSimpleSelect.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { allMockConfigs, mockChatConfigs, mockProviderAtom, type MockProvider } from "@/config.mock.ts";
import { resetConfigOverridesAtom, resetConfigOverridesTitle, updateConfigOverrideAtom } from "@/config.override.ts";
import { useConfig } from "@/config.ts";
import type { SiteConfig } from "@/config.types.ts";
import { isMockApiEnabled } from "@/mock-api/msw/mswInit.ts";
import { availableLocales } from "@/modules/l10n/availableLocales.ts";
import { debugBarVisibleAtom } from "@/modules/main/debug/DebugMenu.store.ts";
import SiteThemeSelect from "@/modules/main/debug/SiteThemeSelect.tsx";
import { cn } from "@/utils/classNames.ts";
import { hasOwn } from "@warrenio/utils/collections/getOwnProperty";
import { useAtom, useAtomValue, useSetAtom } from "jotai/react";
import stringify from "json-stringify-pretty-compact";
import React, { Suspense, useMemo, useState, type FormEvent, type PropsWithChildren, type ReactNode } from "react";
import { Input, Label, NumberField, TextArea, TextField } from "react-aria-components";
import { entries } from "remeda";

// Lazy load to prevent bundling mock DB in production (since the debug menu is still partially enabled in prod)
const MockConfig = React.lazy(() => import("@/modules/main/debug/MockConfig.tsx"));
const MockDbEditor = React.lazy(() => import("@/modules/main/debug/MockDbEditor.tsx"));

//#region Debug bar

export default function DebugBar() {
    const visible = useAtomValue(debugBarVisibleAtom);

    if (!visible) {
        return null;
    }
    const mockEnabled = !import.meta.env.PROD && isMockApiEnabled();
    return (
        <div className={cn(C.DebugBar, "flex flex-col gap-0.5rem")}>
            <h3 className="flex text-muted items-center gap-0.5rem">
                Debug <ResetButton />
            </h3>

            <Suspense>
                <ProviderChoice />
                <Heading>Mock DB {!mockEnabled && " - Disabled"}</Heading>
                {mockEnabled && <MockConfig />}

                <Heading>I18N</Heading>
                <div className="flex flex-row gap-0.5rem children:grow">
                    <LocaleSelect />
                    <CurrencySelect />
                </div>

                <Heading>Config</Heading>

                <div className="HStack children:grow gap-2">
                    <div className="grow-0 px-2">
                        <MaskIcon className="i-lucide:brush size-0.875rem" />
                    </div>
                    <SiteThemeSelect />
                    <ChatProviderSelector />
                </div>

                <Heading>Enabled modules</Heading>
                <ConfigToggleModuleChoice
                    items={[
                        "vmsEnabled",
                        "bareMetalEnabled",
                        "disksEnabled",
                        "vmAppsEnabled",
                        "ipsEnabled",
                        "privateNetworksEnabled",
                        "loadBalancerEnabled",
                        "firewallEnabled",
                        "storageEnabled",
                        "servicesEnabled",
                        "kubernetesEnabled",
                        "chatEnabled",
                    ]}
                />

                {mockEnabled && (
                    <Suspense>
                        <Heading>DB editor</Heading>
                        <MockDbEditor />
                    </Suspense>
                )}

                <Heading>Compute</Heading>
                <JsonTextArea field="predefinedPackages" rows={12} />

                {/* <JsonTextArea label="chatConfig" value={chatConfig} /> */}
                {/* <JsonTextArea label="chatSecrets" value={chatSecrets} /> */}

                <Heading>Payment</Heading>
                <ConfigToggleModuleChoice items={["recurringPaymentsEnabled", "referralCodeEnabled", "allowDebt"]} />
                <FirstAccountCreditChoice />
                <JsonTextArea field="referralCreditAmounts" />
                <JsonTextArea field="limitedBillingAccountConfig" rows={3} />
                <JsonTextArea field="billingAccountFields" rows={8} />
                <JsonTextArea field="displayCurrencies" />

                <Heading>Misc</Heading>
                <ConfigToggleModuleChoice items={["s3ForcePathStyle"]} />

                <Heading>Analytics</Heading>
                <JsonTextArea field="google_tag_manager_key" rows={1} />
                <JsonTextArea field="google_analytics_key" rows={1} />

                <Heading>Locations</Heading>
                <JsonTextArea field="locations" rows={10} />
                {/* <ChangeApiKey /> */}
            </Suspense>
        </div>
    );
}

function Heading({ children }: PropsWithChildren) {
    return <div className={`text-muted b-b b-b-solid b-gray-3 p-b-0.2em ${C.UnPad}`}>{children}</div>;
}

function Labeled({ label, children }: PropsWithChildren<{ label: ReactNode }>) {
    return (
        <div className="flex flex-row items-center gap-2">
            <span>{label}</span>
            {children}
        </div>
    );
}

function ProviderChoice() {
    const [provider, setProvider] = useAtom<MockProvider>(mockProviderAtom);

    const items = entries(allMockConfigs).map(([value, config]) => ({
        value,
        label: `${config.siteName} [${value}]`,
    }));

    return (
        <Labeled label="Provider">
            <WSimpleSelect className="flex-grow" value={provider} items={items} onChange={setProvider} />
        </Labeled>
    );
}

function ChatProviderSelector() {
    const updateConfig = useSetAtom(updateConfigOverrideAtom);
    const [selectedProvider, setSelectedProvider] = useState<keyof typeof mockChatConfigs>();

    const items = entries(mockChatConfigs).map(([key, _config]) => ({
        value: key,
        label: key,
    }));

    const handleChange = (provider: keyof typeof mockChatConfigs) => {
        setSelectedProvider(provider);
        updateConfig((config) => {
            config.chatConfig = mockChatConfigs[provider];
        });
    };

    return <WSimpleSelect placeholder="Chat provider" value={selectedProvider} items={items} onChange={handleChange} />;
}

function FirstAccountCreditChoice() {
    const { firstAccountCredit } = useConfig();
    const updateConfig = useSetAtom(updateConfigOverrideAtom);
    const [value, setValue] = useState<number>(firstAccountCredit);

    function onInput(event: FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        const value = parseFloat(target.value);

        setValue(value);
        updateConfig((config) => {
            config.firstAccountCredit = value;
        });
    }

    return (
        <div>
            <NumberField className={TF.NumberField} minValue={0} value={value} onInput={onInput}>
                <Label className={TF.Label}>firstAccountCredit</Label>
                <Input className={TF.Input} />
            </NumberField>
        </div>
    );
}

interface JsonTextAreaProps {
    field: keyof SiteConfig;
    rows?: number;
}

function JsonTextArea({ field, rows = 6 }: JsonTextAreaProps) {
    //#region Hooks
    const config = useConfig();
    const configValue = config[field];
    const [configOverrides, updateConfigOverride] = useAtom(updateConfigOverrideAtom);
    const hasOverride = hasOwn(configOverrides, field);

    const prettyText = useMemo(() => stringify(configValue, { indent: 2, maxLength: 60 }), [configValue]);

    /** `undefined` if the user has not edited it yet */
    const [text, setText] = useState<string | undefined>(undefined);
    const [isInvalid, setIsInvalid] = useState<boolean>(false);
    //#endregion

    // Keep updating the value based on the config until the user changes it
    const value = text ?? prettyText;

    function changeText(text: string) {
        setText(text);

        let value: any;
        try {
            value = text ? JSON.parse(text) : undefined;
            setIsInvalid(false);
        } catch (_e) {
            setIsInvalid(true);
        }

        updateConfigOverride((config) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            config[field] = value;
        });
    }

    return (
        <div className={C.Textarea}>
            <TextField className={TF.TextField} value={value} onChange={changeText} isInvalid={isInvalid}>
                <Label className={cn("mb-1", isInvalid && "text-error")}>
                    {field}:{hasOverride && <span className="text-warning"> ★</span>}
                </Label>
                <TextArea className={TF.TextArea} rows={rows} />
            </TextField>
        </div>
    );
}

function ConfigToggleModuleChoice({ items }: { items: (keyof SiteConfig)[] }) {
    const [config, updateConfig] = useAtom(updateConfigOverrideAtom);

    return (
        <div className={C.FlexList}>
            {items.map((item) => (
                <SimpleSwitch
                    key={item}
                    value={config[item] as unknown as boolean}
                    onChange={(value) =>
                        updateConfig((config) => {
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                            config[item] = value as any;
                        })
                    }
                >
                    {item}
                </SimpleSwitch>
            ))}
        </div>
    );
}

function CurrencySelect() {
    const [config, updateConfig] = useAtom(updateConfigOverrideAtom);

    const currencies = [undefined, "USD", "EUR", "JPY", "IDR", "THB", "ZAR", "TND"].map(toSimpleItem);

    return (
        <WSimpleSelect
            valueClassName="min-w-5em"
            placeholder="Currency"
            value={config.siteCurrency}
            items={currencies}
            onChange={(value) =>
                updateConfig((config) => {
                    config.siteCurrency = value;
                })
            }
        />
    );
}

function LocaleSelect() {
    const [config, updateConfig] = useAtom(updateConfigOverrideAtom);

    const locales = [undefined, ...availableLocales].map(toSimpleItem);

    return (
        <WSimpleSelect
            valueClassName="min-w-5em"
            placeholder="Locale"
            value={config.siteLocale}
            items={locales}
            onChange={(value) =>
                updateConfig((config) => {
                    config.siteLocale = value;
                })
            }
        />
    );
}

function ResetButton() {
    const resetConfig = useSetAtom(resetConfigOverridesAtom);
    return (
        <WTooltipButton
            variant="ghost"
            icon="i-lucide:list-restart"
            action={resetConfig}
            title={resetConfigOverridesTitle}
        />
    );
}
